import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { environment } from '../../../../../environments/environment';
import { StepperComponent, StepperStep } from '../../primeng';
import { ModalComponent } from '../../primeng/modal/modal.component';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { getDropdownShouldOpenUpward } from '../../../config/dropdown.config';
import { CatalogueApiService, AuthApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../../services/toaster.service';
import {
  buildTanuhDatasetItemPayload,
  buildTanuhDatasetMetrics,
  distributionsToPlotFormValues,
  slugifyMetricName,
  DistributionPlotFormValue,
  extractCreatedItemId,
} from '../../../utils/tanuh-dataset-item-payload.util';
import {
  buildCroissantDocument,
  CROISSANT_DATA_TYPES,
  CroissantDistributionFormValue,
  CroissantFieldFormValue,
  CroissantFormValues,
  CroissantRecordSetFormValue,
  CroissantValidationResult,
  emptyCroissantFormValues,
  emptyDistribution,
  emptyField,
  emptyRecordSet,
  JsonRecord,
  validateCroissant,
} from '../../../utils/croissant.util';
import {
  EvaluatorMetricOption,
  getEvaluatorMetricOptions,
  resolveEvaluatorMetric,
} from '../../../utils/evaluator-metrics.util';
import { formatProblemStatement } from '../../../../catalogue/reference-datasets/hackathon-datasets.data';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted';
  problemStatement?: string;
}

/** Number of empty custom-metric cards shown when the section is first opened. */
export const DEFAULT_CUSTOM_METRICS_COUNT = 3;

export interface CustomMetricFormValue {
  name: string;
  min: string;
  max: string;
}

/** Accepts both the legacy string[] shape and the current {name,min,max} shape from localStorage. */
export function normalizeCustomMetricEntry(entry: unknown): CustomMetricFormValue {
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    return {
      name: String(record['name'] ?? ''),
      min: record['min'] != null ? String(record['min']) : '',
      max: record['max'] != null ? String(record['max']) : '',
    };
  }
  return { name: String(entry ?? ''), min: '', max: '' };
}

/** A metric's min must not exceed its max when both are provided. */
export function customMetricRangeValidator(group: AbstractControl): ValidationErrors | null {
  const min = group.get('min')?.value;
  const max = group.get('max')?.value;
  if (min === '' || min == null || max === '' || max == null) {
    return null;
  }
  const minNum = Number(min);
  const maxNum = Number(max);
  if (!Number.isFinite(minNum) || !Number.isFinite(maxNum)) {
    return null;
  }
  return minNum > maxNum ? { rangeOrder: true } : null;
}

/** A metric name must be one the selected problem statement's evaluator reports. */
export function evaluatorMetricNameValidator(problemStatement: () => string) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value || resolveEvaluatorMetric(problemStatement(), value)) {
      return null;
    }
    return { metricNotAllowed: true };
  };
}

/**
 * Snap each metric name to its dropdown option for `problemStatement` (an uploaded Croissant file
 * may spell it differently, e.g. `Sensitivity (Recall)`), carrying the primary / secondary
 * selections over to the option's key. Names with no option are kept as they are, so the form can
 * flag them.
 */
export function alignMetricsToEvaluator(
  problemStatement: string,
  values: { customMetrics: CustomMetricFormValue[]; primaryMetric: string; secondaryMetric: string },
): { customMetrics: CustomMetricFormValue[]; primaryMetric: string; secondaryMetric: string } {
  const keysOf = (metrics: CustomMetricFormValue[]): string[] => {
    const seen = new Set<string>();
    return metrics.map((metric, index) => {
      const name = (metric.name ?? '').trim();
      if (!name) {
        return '';
      }
      let key = slugifyMetricName(name, index);
      while (seen.has(key)) {
        key = `${key}_${index + 1}`;
      }
      seen.add(key);
      return key;
    });
  };
  const customMetrics = values.customMetrics.map((metric) => ({
    ...metric,
    name: resolveEvaluatorMetric(problemStatement, metric.name)?.label ?? metric.name,
  }));
  const before = keysOf(values.customMetrics);
  const after = keysOf(customMetrics);
  const carry = (selected: string): string => {
    const index = selected ? before.indexOf(selected) : -1;
    return index >= 0 ? after[index] : selected;
  };
  return {
    customMetrics,
    primaryMetric: carry(values.primaryMetric),
    secondaryMetric: carry(values.secondaryMetric),
  };
}

export interface DatasetDetailField {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'textarea' | 'metric-dropdown';
  required?: boolean;
  maxLength?: number;
}

export const DATASET_DETAIL_FIELDS: DatasetDetailField[] = [
  { key: 'modality', label: 'Modality', placeholder: 'e.g. RGB image' },
  { key: 'volume', label: 'Volume', placeholder: 'e.g. 1,200 annotated images' },
  { key: 'output', label: 'Output', placeholder: 'e.g. Binary classification' },
  {
    key: 'classNames',
    label: 'Class names',
    placeholder: 'e.g. Non-Suspicious, Suspicious (comma-separated, in label order)',
    required: false,
    maxLength: 500,
  },
];

/** Rendered after "Benchmark metrics" so options are drawn from the metrics the user just named. */
export const DATASET_METRIC_SELECTION_FIELDS: DatasetDetailField[] = [
  { key: 'primaryMetric', label: 'Primary metric', placeholder: 'Select primary metric', type: 'metric-dropdown' },
  { key: 'secondaryMetric', label: 'Secondary metric', placeholder: 'Select secondary metric', type: 'metric-dropdown' },
];

export const DATASET_INPUT_FIELDS: DatasetDetailField[] = [
  { key: 'inputDataType', label: 'Data type', placeholder: 'e.g. Single RGB 2D intra-oral photograph' },
  { key: 'inputSize', label: 'Size', placeholder: 'e.g. ~3–5 MB per file · 3072×3072 px' },
  {
    key: 'inputMaxFileSize',
    label: 'Max file size',
    placeholder: 'e.g. ≤50 MB per DICOM file',
    required: false,
  },
  { key: 'inputFormat', label: 'Format', placeholder: 'e.g. JPG or DICOM (.dcm)' },
  {
    key: 'inputOrganization',
    label: 'Organization',
    placeholder: 'e.g. Two folders by binary class',
    type: 'textarea',
    maxLength: 1000,
  },
  {
    key: 'inputPreprocessing',
    label: 'Pre-processing',
    placeholder: 'e.g. No additional pre-processing applied',
    type: 'textarea',
    maxLength: 1000,
  },
];

export const DATASET_OUTPUT_FIELDS: DatasetDetailField[] = [
  { key: 'outputDataType', label: 'Data type', placeholder: 'e.g. Binary classification label' },
  { key: 'outputFormat', label: 'Format', placeholder: 'e.g. CSV or Excel' },
  {
    key: 'outputMaxFileSize',
    label: 'Max file size',
    placeholder: 'e.g. ≤5 MB',
    required: false,
  },
  { key: 'outputColumns', label: 'Columns', placeholder: 'e.g. Unique_case_ID, Label' },
  {
    key: 'outputDescription',
    label: 'Description',
    placeholder: 'Describe label encoding and file linkage',
    type: 'textarea',
    maxLength: 2000,
  },
];

@Component({
  selector: 'app-provider-metadata',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
    ModalComponent,
  ],
  templateUrl: './metadata.component.html',
  styleUrl: './metadata.component.scss',
})
export class ProviderMetadataComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly catalogueApiService = inject(CatalogueApiService);
  private readonly authApiService = inject(AuthApiService);
  private readonly toasterService = inject(ToasterService);
  private destroy$ = new Subject<void>();
  isCreatingItem = false;

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  metadataForm!: FormGroup;
  currentStep = 1; // Step 2 (0-based index) - Enter Metadata
  totalSteps = 4;
  
  @ViewChild('step2Template', { static: true }) step2Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.isModel() ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => (i === 0 ? { ...step, label } : step));
  }

  private configureStepperSteps(): void {
    if (this.isModel()) {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your Models' },
        { key: 'step2', label: 'Enter Metadata' },
        { key: 'step3', label: 'Additional Info' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.totalSteps = 4;
      this.currentStep = 1;
    } else {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your datasets' },
        { key: 'step2', label: 'Enter Metadata' },
        { key: 'step3', label: 'Additional Metadata' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.totalSteps = 4;
      this.currentStep = this.isBenchmarkPhase() ? 2 : 1;
    }
  }

  private getRouteBase(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/org-admin/add-model')) {
      return 'org-admin/add-model';
    }
    if (currentUrl.includes('/org-admin/add-dataset')) {
      return 'org-admin/add-dataset';
    }
    if (currentUrl.includes('/add-model/')) {
      return 'provider/add-contributions/add-model';
    }
    if (currentUrl.includes('/add-contributions/dataset')) {
      return 'provider/add-contributions/dataset';
    }
    return 'provider/add-contributions/add-dataset';
  }

  datasetData: DatasetData | null = null;
  readonly datasetDetailFields = DATASET_DETAIL_FIELDS;
  readonly datasetMetricSelectionFields = DATASET_METRIC_SELECTION_FIELDS;
  readonly datasetInputFields = DATASET_INPUT_FIELDS;
  readonly datasetOutputFields = DATASET_OUTPUT_FIELDS;
  isLicenseDropdownOpen = false;
  isResourceServerDropdownOpen = false;
  isUploadFrequencyDropdownOpen = false;
  isIndustryDropdownOpen = false;
  isPrimaryMetricDropdownOpen = false;
  isSecondaryMetricDropdownOpen = false;
  /** Index of the benchmark metric card whose name dropdown is open, if any. */
  openCustomMetricDropdownIndex: number | null = null;
  /** Problem statement chosen on the setup step; decides which metrics the form offers. */
  problemStatement = '';

  readonly croissantDataTypes = CROISSANT_DATA_TYPES;
  /** Live result of validating the Croissant document the form currently describes (datasets only). */
  croissantIssues: CroissantValidationResult = { errors: [], warnings: [] };
  /** The Croissant file the user uploaded, if any; properties the builder has no control for are kept from it. */
  private croissantBase: JsonRecord | null = null;
  /** True once the form holds Croissant values (typed, or pre-filled from an upload). */
  showCroissantIssues = false;

  private readonly collapsedKeys = new Set<string>();

  isCollapsed(key: string): boolean {
    return this.collapsedKeys.has(key);
  }

  /** Google-Docs-style outline pane. */
  activeIndexId = '';
  private scrollFrame = 0;
  /** Index entry the user clicked; scroll-spy is paused until the smooth scroll to it settles. */
  private scrollLockTarget: string | null = null;
  private scrollLockTimer: ReturnType<typeof setTimeout> | null = null;

  private lockScrollSpy(targetId: string, settleMs: number): void {
    this.scrollLockTarget = targetId;
    if (this.scrollLockTimer) {
      clearTimeout(this.scrollLockTimer);
    }
    this.scrollLockTimer = setTimeout(() => {
      this.scrollLockTimer = null;
      const target = document.getElementById(targetId);
      this.scrollLockTarget = null;
      // Sections near the end of the page can't reach the top edge; keep the clicked one if it is in view
      const top = target?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      if (!(top >= -20 && top < window.innerHeight - 40)) {
        this.updateActiveIndexItem();
      }
    }, settleMs);
  }

  private readonly onScroll = (): void => {
    if (this.scrollLockTarget) {
      this.lockScrollSpy(this.scrollLockTarget, 120);
      return;
    }
    if (this.scrollFrame) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateActiveIndexItem();
    });
  };

  get indexItems(): Array<{ id: string; label: string; level: 1 | 2; collapseKey: string }> {
    if (this.isModel() || !this.metadataForm) {
      return [];
    }
    const section = (id: string, label: string) => ({ id, label, level: 1 as const, collapseKey: id });
    const cards = (prefix: string, label: string, count: number) =>
      Array.from({ length: count }, (_, i) => ({
        id: `card-${prefix}-${i}`,
        label: `${label} ${i + 1}`,
        level: 2 as const,
        collapseKey: `${prefix}-${i}`,
      }));
    if (this.isBenchmarkPhase()) {
      return [
        section('dataset-details-heading', 'Dataset characteristics'),
        section('dataset-input-heading', 'Input'),
        section('dataset-output-heading', 'Output'),
        section('dataset-metrics-heading', 'Benchmark metrics'),
        section('dataset-metric-selection-heading', 'Primary & secondary metrics'),
        section('dataset-plots-heading', 'Statistics plots'),
        ...cards('plot', 'Plot', this.distributionPlots.length),
      ];
    }
    return [
      section('croissant-core-heading', 'Croissant metadata'),
      section('croissant-distribution-heading', 'Files (distribution)'),
      ...cards('dist', 'File', this.croissantDistributions.length),
      section('croissant-recordset-heading', 'Record sets'),
      ...cards('rs', 'Record set', this.croissantRecordSets.length),
    ];
  }

  /** JSON preview: 'view' from the index pane, 'confirm' as the last check before the upload step. */
  isPreviewOpen = false;
  previewMode: 'view' | 'confirm' = 'view';
  previewTab: 'croissant' | 'benchmark' = 'croissant';
  previewJson = { croissant: '', benchmark: '' };

  get previewText(): string {
    return this.previewTab === 'croissant' ? this.previewJson.croissant : this.previewJson.benchmark;
  }

  openPreview(mode: 'view' | 'confirm'): void {
    this.previewMode = mode;
    this.previewTab = 'croissant';
    this.previewJson = {
      croissant: JSON.stringify(this.buildCurrentCroissantDocument(), null, 2),
      benchmark: JSON.stringify(buildTanuhDatasetMetrics({ ...this.metadataForm.value }), null, 2),
    };
    this.isPreviewOpen = true;
  }

  async copyPreview(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.previewText);
      this.toasterService.success('JSON copied to clipboard');
    } catch {
      this.toasterService.error('Could not copy to the clipboard');
    }
  }

  confirmPreview(): void {
    if (this.previewMode === 'confirm') {
      void this.proceed();
    }
  }

  scrollToIndexItem(item: { id: string; collapseKey: string }): void {
    this.activeIndexId = item.id;
    this.lockScrollSpy(item.id, 300);
    // Expand the target (and, for a card, its section) so there is something to land on
    this.collapsedKeys.delete(item.collapseKey);
    if (item.id.startsWith('card-')) {
      this.collapsedKeys.delete(
        item.id.startsWith('card-plot') ? 'dataset-plots-heading'
          : item.id.startsWith('card-dist') ? 'croissant-distribution-heading'
          : 'croissant-recordset-heading',
      );
    }
    this.cdr.detectChanges();
    document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private updateActiveIndexItem(): void {
    let active = '';
    for (const item of this.indexItems) {
      const el = document.getElementById(item.id);
      if (el && el.getBoundingClientRect().top <= 140) {
        active = item.id;
      }
    }
    if (active && active !== this.activeIndexId) {
      this.activeIndexId = active;
      this.cdr.markForCheck();
    }
  }

  toggleCollapsed(key: string): void {
    if (!this.collapsedKeys.delete(key)) {
      this.collapsedKeys.add(key);
    }
  }
  
  // Track if dropdowns should open upward
  isLicenseDropdownUpward = false;
  isResourceServerDropdownUpward = false;
  isUploadFrequencyDropdownUpward = false;
  isIndustryDropdownUpward = false;

  licenseOptions: Array<{ value: string; label: string }> = [
    { value: 'Open Government License, India', label: 'Open Government License, India' },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'Other', label: 'Other' }
  ];

  industryOptions: Array<{ value: string; label: string }> = [
    { value: 'Agriculture', label: 'Agriculture' },
    { value: 'forest', label: 'Forest' },
    { value: 'Water', label: 'Water' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Transport', label: 'Transport' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Other', label: 'Other' }
  ];

  resourceServerOptions: Array<{ value: string; label: string; displayLabel: string; name: string; url: string }> = [];

  uploadFrequencyOptions: Array<{ value: string; label: string }> = [
    { value: 'OneTime', label: 'OneTime' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'Annually', label: 'Annually' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  removeProtocol(url: string): string {
    return url.replace(/^https?:\/\//, '');
  }

  private initializeResourceServerOptions(): void {
    try {
      // Initialize resource server options from environment
      const env = environment as any;
      
      const resourceServers = [
        { name: 'OGC', url: env.ogcApiPath || '' },
        { name: 'NGSI-LD', url: env.ngsiLdApiPath || '' },
        { name: 'GATEWAY', url: env.gatewayApiPath || '' }
      ];
      
      this.resourceServerOptions = resourceServers
        .filter(server => server.url && server.url.trim() !== '') // Only include servers with valid URLs
        .map((server: { name: string; url: string }) => ({
          value: server.url,
          name: server.name,
          url: this.removeProtocol(server.url),
          label: `${server.name} (${this.removeProtocol(server.url)})`,
          displayLabel: `${server.name} (${this.removeProtocol(server.url)})`
        }));
      
      // Fallback: If no options loaded, use hardcoded values
      if (this.resourceServerOptions.length === 0) {
        this.resourceServerOptions = [
          { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
          { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
          { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' }
        ];
      }
    } catch (error) {
      // Fallback to hardcoded values on error
      this.resourceServerOptions = [
        { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
        { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
        { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' }
      ];
    }
  }

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  /**
   * Check if current route is for model creation
   */
  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  /**
   * Datasets split this form over two steps: Croissant metadata (step 2) and the benchmark
   * details (step 3, "Additional Metadata"). Both steps share one form and one localStorage entry.
   */
  isBenchmarkPhase(): boolean {
    if (this.isModel()) {
      return false;
    }
    // router.url can still be the previous page while this component initialises (e.g. when
    // coming back from the upload step), so prefer the route this instance was created for.
    const routePath = this.route.snapshot?.routeConfig?.path;
    return (routePath ?? this.router.url).includes('additionalInfo');
  }

  private static readonly BENCHMARK_ARRAY_CONTROLS = ['customMetrics', 'distributionPlots'];

  private phaseControlNames(): string[] {
    const benchmarkNames = new Set<string>([
      ...DATASET_DETAIL_FIELDS.map((f) => f.key),
      ...DATASET_METRIC_SELECTION_FIELDS.map((f) => f.key),
      ...DATASET_INPUT_FIELDS.map((f) => f.key),
      ...DATASET_OUTPUT_FIELDS.map((f) => f.key),
      ...ProviderMetadataComponent.BENCHMARK_ARRAY_CONTROLS,
    ]);
    const wantBenchmark = this.isBenchmarkPhase();
    return Object.keys(this.metadataForm.controls).filter(
      (name) => benchmarkNames.has(name) === wantBenchmark,
    );
  }

  /** Validity of only the controls shown on the current step (datasets). */
  private isPhaseFormValid(): boolean {
    return this.phaseControlNames().every((name) => this.metadataForm.get(name)?.valid);
  }

  private markPhaseTouched(): void {
    this.phaseControlNames().forEach((name) => this.metadataForm.get(name)?.markAllAsTouched());
  }

  ngOnInit(): void {
    this.configureStepperSteps();

    // Initialize resource server options from environment FIRST
    this.initializeResourceServerOptions();
    
    // Get the correct localStorage key based on route
    const localStorageKey = this.getLocalStorageKey();
    
    // Get dataset/model data from router state or localStorage
    // Try to get from navigation state first (during navigation)
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state && navigation.extras.state['datasetData']) {
      this.datasetData = navigation.extras.state['datasetData'] as DatasetData;
      // Also save to localStorage for persistence
      localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
    } else {
      // Try to get from history state (after navigation completes)
      const state = window.history.state;
      if (state && state.datasetData) {
        this.datasetData = state.datasetData as DatasetData;
        // Also save to localStorage for persistence
        localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
      } else {
        // Fallback: get from localStorage (for page refresh or backward navigation)
        const savedData = localStorage.getItem(localStorageKey);
        if (savedData) {
          try {
            this.datasetData = JSON.parse(savedData) as DatasetData;
          } catch (error) {
            console.error('Error parsing saved data:', error);
            localStorage.removeItem(localStorageKey);
          }
        }
      }
    }

    // Restore metadata form data from localStorage if available
    const savedData = localStorage.getItem(localStorageKey);
    let savedMetadataData: any = {};
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (this.isModel()) {
          savedMetadataData = {
            shortDescription: parsedData.shortDescription || '',
            longDescription: parsedData.longDescription || '',
            license: parsedData.license || '',
            otherLicense: parsedData.otherLicense || '',
            industry: parsedData.industry || ''
          };
        } else {
          const metricsRecord =
            parsedData.datasetMetrics && typeof parsedData.datasetMetrics === 'object'
              ? parsedData.datasetMetrics
              : {};
          const base = parsedData.croissantBase;
          this.croissantBase =
            base && typeof base === 'object' && !Array.isArray(base) ? (base as JsonRecord) : null;
          this.problemStatement = String(parsedData.problemStatement ?? '').trim();
          savedMetadataData = {
            ...this.restoreCroissantFormValues(parsedData),
            ...Object.fromEntries(
              DATASET_DETAIL_FIELDS.map(({ key }) => [key, parsedData[key] ?? '']),
            ),
            ...Object.fromEntries(
              DATASET_METRIC_SELECTION_FIELDS.map(({ key }) => [key, parsedData[key] ?? '']),
            ),
            ...Object.fromEntries(
              DATASET_INPUT_FIELDS.map(({ key }) => [key, parsedData[key] ?? '']),
            ),
            ...Object.fromEntries(
              DATASET_OUTPUT_FIELDS.map(({ key }) => [key, parsedData[key] ?? '']),
            ),
            customMetrics: Array.isArray(parsedData.customMetrics)
              ? parsedData.customMetrics.map((entry: unknown) => normalizeCustomMetricEntry(entry))
              : [],
            distributionPlots: distributionsToPlotFormValues(
              parsedData.distributionPlots ??
                metricsRecord['distributions'] ??
                parsedData.distributions,
            ),
          };
          Object.assign(
            savedMetadataData,
            alignMetricsToEvaluator(this.problemStatement, {
              customMetrics: savedMetadataData.customMetrics,
              primaryMetric: String(savedMetadataData.primaryMetric ?? ''),
              secondaryMetric: String(savedMetadataData.secondaryMetric ?? ''),
            }),
          );
        }
      } catch (error) {
        console.error('Error parsing saved metadata data:', error);
      }
    }

    // Initialize form based on whether it's a model or dataset
    if (this.isModel()) {
      // For models: Short/Long Description (min 50 chars) + Industry and License
      this.metadataForm = this.fb.group({
        shortDescription: [savedMetadataData.shortDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(200)]],
        longDescription: [savedMetadataData.longDescription || '', [Validators.required, Validators.minLength(50), Validators.maxLength(2000)]],
        industry: [savedMetadataData.industry || '', [Validators.required]],
        license: [savedMetadataData.license || '', [Validators.required]],
        otherLicense: [savedMetadataData.otherLicense || '']
      });
    } else {
      const allDetailFields = [
        ...DATASET_DETAIL_FIELDS,
        ...DATASET_METRIC_SELECTION_FIELDS,
        ...DATASET_INPUT_FIELDS,
        ...DATASET_OUTPUT_FIELDS,
      ];
      const detailControls = Object.fromEntries(
        allDetailFields.map((field) => {
          const validators = [
            ...(field.required === false ? [] : [Validators.required]),
            Validators.maxLength(field.maxLength ?? 200),
          ];
          return [field.key, [savedMetadataData[field.key] ?? '', validators]];
        }),
      );
      const savedCustomMetrics = savedMetadataData['customMetrics'] as
        | CustomMetricFormValue[]
        | undefined;
      const initialCustomMetrics =
        savedCustomMetrics && savedCustomMetrics.length > 0
          ? savedCustomMetrics
          : this.buildDefaultCustomMetrics();
      const croissantValues = savedMetadataData as CroissantFormValues;
      this.metadataForm = this.fb.group({
        ...detailControls,
        croissantDescription: [croissantValues.croissantDescription ?? '', [Validators.required, Validators.maxLength(5000)]],
        croissantVersion: [croissantValues.croissantVersion ?? '1.0.0', [Validators.maxLength(32)]],
        croissantLicense: [croissantValues.croissantLicense ?? '', [Validators.maxLength(512)]],
        croissantUrl: [croissantValues.croissantUrl ?? '', [Validators.maxLength(512), Validators.pattern(/^https?:\/\/\S+$/)]],
        croissantCiteAs: [croissantValues.croissantCiteAs ?? '', [Validators.maxLength(1000)]],
        croissantCreator: [croissantValues.croissantCreator ?? '', [Validators.maxLength(512)]],
        croissantDatePublished: [
          croissantValues.croissantDatePublished ?? '',
          [Validators.pattern(/^\d{4}-\d{2}-\d{2}$/)],
        ],
        croissantKeywords: [croissantValues.croissantKeywords ?? '', [Validators.maxLength(500)]],
        croissantDistributions: this.fb.array(
          (croissantValues.croissantDistributions ?? [emptyDistribution()]).map((entry) =>
            this.buildDistributionGroup(entry),
          ),
        ),
        croissantRecordSets: this.fb.array(
          (croissantValues.croissantRecordSets ?? [emptyRecordSet()]).map((entry) =>
            this.buildRecordSetGroup(entry),
          ),
        ),
        customMetrics: this.fb.array(
          initialCustomMetrics.map((metric) => this.buildCustomMetricGroup(metric)),
        ),
        distributionPlots: this.fb.array(
          (savedMetadataData.distributionPlots as DistributionPlotFormValue[] | undefined)?.map(
            (plot) => this.buildDistributionPlotGroup(plot),
          ) ?? [],
        ),
      });
    }

    // Values restored from storage (typed earlier, or pre-filled from an uploaded Croissant file)
    // should show their validation state straight away.
    if (!this.isModel() && savedMetadataData['croissantDescription']) {
      this.croissantDistributions.markAllAsTouched();
      this.croissantRecordSets.markAllAsTouched();
      this.showCroissantIssues = true;
    }
    this.refreshCroissantIssues();

    // Mark custom metric controls touched if restored with a value
    if (!this.isModel()) {
      this.customMetrics.controls.forEach((control) => {
        if ((control.get('name') as FormControl)?.value) {
          control.markAllAsTouched();
        }
      });
    }

    // Mark form controls as touched if they have values (for proper validation state)
    Object.keys(savedMetadataData).forEach(key => {
      if (savedMetadataData[key] !== '' && savedMetadataData[key] != null && this.metadataForm.get(key)) {
        this.metadataForm.get(key)?.markAsTouched();
      }
    });

    // Watch for license changes to add/remove validation for otherLicense (models only)
    if (this.isModel()) {
      this.metadataForm.get('license')?.valueChanges.subscribe(value => {
        const otherLicenseControl = this.metadataForm.get('otherLicense');
        if (value === 'Other') {
          otherLicenseControl?.setValidators([Validators.required]);
        } else {
          otherLicenseControl?.clearValidators();
          otherLicenseControl?.setValue('');
        }
        otherLicenseControl?.updateValueAndValidity();
      });
    }

    // Auto-save form data on changes (debounced)
    this.metadataForm.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.refreshCroissantIssues();
        this.saveMetadataFormData();
      });
  }

  ngAfterViewInit(): void {
    document.addEventListener('scroll', this.onScroll, true);
    // Update step 2 with template reference after view is initialized
    if (this.step2Template) {
      this.stepperSteps[this.currentStep].content = this.step2Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    // Save form data before navigating
    this.saveMetadataFormData();
    
    const routeBase = this.getRouteBase();
    const routes = this.isModel()
      ? [
          routeBase,
          `${routeBase}/metadata`,
          `${routeBase}/additionalInfo`,
          `${routeBase}/uploads`
        ]
      : [
          routeBase,
          `${routeBase}/metadata`,
          `${routeBase}/additionalInfo`,
          `${routeBase}/uploads`
        ];

    if (!this.isModel()) {
      if (stepIndex === 2 && !this.isBenchmarkPhase() && !this.isMetadataFormValid()) {
        return;
      }
      if (stepIndex === 3 && !this.hasCreatedItemId()) {
        return;
      }
    }
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  isMetadataFormValid(): boolean {
    if (this.isModel()) {
      return this.metadataForm.valid;
    }
    if (this.isBenchmarkPhase()) {
      return this.isPhaseFormValid();
    }
    // Datasets, step 2: the Croissant fields must be filled and the document itself valid
    return this.isPhaseFormValid() && this.croissantIssues.errors.length === 0;
  }

  onNext(): void {
    if (!this.isMetadataFormValid()) {
      return;
    }
    if (this.isBenchmarkPhase()) {
      // Last step before the item is created: let the user check the JSON first
      this.openPreview('confirm');
      return;
    }
    void this.proceed();
  }

  onPrevious(): void {
    // Navigate to previous step (dataset)
    this.goBack();
  }

  isOtherLicenseSelected(): boolean {
    return this.metadataForm.get('license')?.value === 'Other';
  }

  toggleLicenseDropdown(): void {
    if (!this.isLicenseDropdownOpen) {
      this.isResourceServerDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('license');
    }
    this.isLicenseDropdownOpen = !this.isLicenseDropdownOpen;
  }
  
  /**
   * Check if dropdown should open upward based on available space
   */
  private checkDropdownPosition(dropdownType: 'license' | 'resourceServer' | 'uploadFrequency' | 'industry'): void {
    setTimeout(() => {
      // Find all dropdown wrappers
      const allWrappers = this.elementRef.nativeElement.querySelectorAll('.custom-dropdown-wrapper');
      let targetWrapper: HTMLElement | null = null;
      
      // Find the specific dropdown wrapper by label text
      if (dropdownType === 'industry') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Industry');
        }) as HTMLElement;
      } else if (dropdownType === 'license') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('License');
        }) as HTMLElement;
      } else if (dropdownType === 'resourceServer') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Resource Server');
        }) as HTMLElement;
      } else if (dropdownType === 'uploadFrequency') {
        targetWrapper = Array.from(allWrappers).find(w => {
          const label = (w as HTMLElement).closest('.form-field')?.querySelector('label');
          return label?.textContent?.includes('Upload Frequency');
        }) as HTMLElement;
      }
      
      if (!targetWrapper) return;
      
      const trigger = targetWrapper.querySelector('.custom-dropdown-trigger') as HTMLElement;
      if (!trigger) return;
      
      const rect = trigger.getBoundingClientRect();
      let optionCount = 0;
      if (dropdownType === 'industry') {
        optionCount = this.industryOptions.length;
      } else if (dropdownType === 'license') {
        optionCount = this.licenseOptions.length;
      } else if (dropdownType === 'resourceServer') {
        optionCount = this.resourceServerOptions.length;
      } else if (dropdownType === 'uploadFrequency') {
        optionCount = this.uploadFrequencyOptions.length;
      }
      const shouldOpenUpward = getDropdownShouldOpenUpward(rect, optionCount);
      
      switch (dropdownType) {
        case 'industry':
          this.isIndustryDropdownUpward = shouldOpenUpward;
          break;
        case 'license':
          this.isLicenseDropdownUpward = shouldOpenUpward;
          break;
        case 'resourceServer':
          this.isResourceServerDropdownUpward = shouldOpenUpward;
          break;
        case 'uploadFrequency':
          this.isUploadFrequencyDropdownUpward = shouldOpenUpward;
          break;
      }
      
      this.cdr.detectChanges();
    }, 0);
  }

  selectLicense(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ license: option.value });
    this.isLicenseDropdownOpen = false;
    this.metadataForm.get('license')?.markAsTouched();
  }

  getSelectedLicenseLabel(): string {
    const selectedValue = this.metadataForm.get('license')?.value;
    if (!selectedValue) {
      return 'Select license';
    }
    const selectedOption = this.licenseOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select license';
  }

  isLicenseSelected(value: string): boolean {
    return this.metadataForm.get('license')?.value === value;
  }

  clearLicense(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ license: '' });
    this.metadataForm.get('license')?.markAsTouched();
  }

  // Industry dropdown methods
  toggleIndustryDropdown(): void {
    if (!this.isIndustryDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isResourceServerDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.checkDropdownPosition('industry');
    }
    this.isIndustryDropdownOpen = !this.isIndustryDropdownOpen;
  }

  selectIndustry(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ industry: option.value });
    this.isIndustryDropdownOpen = false;
    this.metadataForm.get('industry')?.markAsTouched();
  }

  getSelectedIndustryLabel(): string {
    const selectedValue = this.metadataForm.get('industry')?.value;
    if (!selectedValue) {
      return 'Select industry';
    }
    const option = this.industryOptions.find(opt => opt.value === selectedValue);
    return option ? option.label : selectedValue;
  }

  isIndustrySelected(value: string): boolean {
    return this.metadataForm.get('industry')?.value === value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isLicenseDropdownOpen = false;
    this.isResourceServerDropdownOpen = false;
    this.isUploadFrequencyDropdownOpen = false;
    this.isIndustryDropdownOpen = false;
    this.isPrimaryMetricDropdownOpen = false;
    this.isSecondaryMetricDropdownOpen = false;
    this.closeCustomMetricDropdown();
  }

  toggleMetricDetailDropdown(key: string): void {
    if (key === 'primaryMetric') {
      this.isPrimaryMetricDropdownOpen = !this.isPrimaryMetricDropdownOpen;
      this.isSecondaryMetricDropdownOpen = false;
    } else {
      this.isSecondaryMetricDropdownOpen = !this.isSecondaryMetricDropdownOpen;
      this.isPrimaryMetricDropdownOpen = false;
    }
    this.isLicenseDropdownOpen = false;
    this.isIndustryDropdownOpen = false;
    this.isResourceServerDropdownOpen = false;
    this.isUploadFrequencyDropdownOpen = false;
    this.closeCustomMetricDropdown();
  }

  selectMetricDetail(fieldKey: string, metricKey: string): void {
    this.metadataForm.get(fieldKey)?.setValue(metricKey);
    this.metadataForm.get(fieldKey)?.markAsTouched();
    if (fieldKey === 'primaryMetric') {
      this.isPrimaryMetricDropdownOpen = false;
    } else {
      this.isSecondaryMetricDropdownOpen = false;
    }
  }

  isMetricDetailDropdownOpen(key: string): boolean {
    return key === 'primaryMetric' ? this.isPrimaryMetricDropdownOpen : this.isSecondaryMetricDropdownOpen;
  }

  isMetricDetailSelected(fieldKey: string, metricKey: string): boolean {
    return this.metadataForm.get(fieldKey)?.value === metricKey;
  }

  getMetricDetailLabel(fieldKey: string): string {
    const val = this.metadataForm.get(fieldKey)?.value as string | undefined;
    return this.metricDropdownOptions.find((f) => f.key === val)?.label ?? val ?? '';
  }

  toggleResourceServerDropdown(): void {
    if (!this.isResourceServerDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('resourceServer');
    }
    this.isResourceServerDropdownOpen = !this.isResourceServerDropdownOpen;
  }

  selectResourceServer(option: { value: string; label: string; displayLabel: string }): void {
    this.metadataForm.patchValue({ resourceServer: option.value });
    this.isResourceServerDropdownOpen = false;
    this.metadataForm.get('resourceServer')?.markAsTouched();
  }

  getSelectedResourceServerLabel(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return 'Select resource server';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    if (selectedOption) {
      return `${selectedOption.name} - ${selectedOption.url}`;
    }
    return 'Select resource server';
  }

  getSelectedResourceServerName(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return '';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.name : '';
  }

  getSelectedResourceServerUrl(): string {
    const selectedValue = this.metadataForm.get('resourceServer')?.value;
    if (!selectedValue) {
      return '';
    }
    const selectedOption = this.resourceServerOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.url : '';
  }

  isResourceServerSelected(value: string): boolean {
    return this.metadataForm.get('resourceServer')?.value === value;
  }

  clearResourceServer(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ resourceServer: '' });
    this.metadataForm.get('resourceServer')?.markAsTouched();
  }

  toggleUploadFrequencyDropdown(): void {
    if (!this.isUploadFrequencyDropdownOpen) {
      this.isLicenseDropdownOpen = false;
      this.isResourceServerDropdownOpen = false;
      this.isIndustryDropdownOpen = false;
      this.checkDropdownPosition('uploadFrequency');
    }
    this.isUploadFrequencyDropdownOpen = !this.isUploadFrequencyDropdownOpen;
  }

  selectUploadFrequency(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ uploadFrequency: option.value });
    this.isUploadFrequencyDropdownOpen = false;
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
  }

  getSelectedUploadFrequencyLabel(): string {
    const selectedValue = this.metadataForm.get('uploadFrequency')?.value;
    if (!selectedValue) {
      return 'Select upload frequency';
    }
    const selectedOption = this.uploadFrequencyOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select upload frequency';
  }

  isUploadFrequencySelected(value: string): boolean {
    return this.metadataForm.get('uploadFrequency')?.value === value;
  }

  clearUploadFrequency(event: Event): void {
    event.stopPropagation();
    this.metadataForm.patchValue({ uploadFrequency: '' });
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
  }

  getShortDescriptionControl(): AbstractControl | null {
    return this.metadataForm.get('shortDescription');
  }

  getLongDescriptionControl(): AbstractControl | null {
    return this.metadataForm.get('longDescription');
  }

  getShortDescriptionLength(): number {
    const value = this.getShortDescriptionControl()?.value || '';
    return value.length;
  }

  getLongDescriptionLength(): number {
    const value = this.getLongDescriptionControl()?.value || '';
    return value.length;
  }

  getShortDescriptionMaxLength(): number {
    return 200;
  }

  getLongDescriptionMaxLength(): number {
    return 2000;
  }

  private buildDefaultCustomMetrics(): CustomMetricFormValue[] {
    return Array.from({ length: DEFAULT_CUSTOM_METRICS_COUNT }, () => ({ name: '', min: '', max: '' }));
  }

  private buildCustomMetricGroup(value: Partial<CustomMetricFormValue> = {}): FormGroup {
    return this.fb.group(
      {
        name: [
          value.name ?? '',
          [
            Validators.required,
            Validators.maxLength(120),
            evaluatorMetricNameValidator(() => this.problemStatement),
          ],
        ],
        min: [value.min ?? '', [Validators.pattern(/^-?\d*\.?\d+$/)]],
        max: [value.max ?? '', [Validators.pattern(/^-?\d*\.?\d+$/)]],
      },
      { validators: customMetricRangeValidator },
    );
  }

  get customMetrics(): FormArray {
    return this.metadataForm.get('customMetrics') as FormArray;
  }

  addCustomMetric(): void {
    this.customMetrics.push(this.buildCustomMetricGroup());
  }

  removeCustomMetric(index: number): void {
    if (this.customMetrics.length > 1) {
      this.closeCustomMetricDropdown();
      this.customMetrics.removeAt(index);
      this.clearStaleMetricSelections();
    }
  }

  /** Metrics the selected problem statement's evaluator reports -- the only names a card may hold. */
  get evaluatorMetricOptions(): readonly EvaluatorMetricOption[] {
    return getEvaluatorMetricOptions(this.problemStatement);
  }

  get problemStatementLabel(): string {
    return formatProblemStatement(this.problemStatement) ?? '';
  }

  toggleCustomMetricDropdown(index: number): void {
    const opening = this.openCustomMetricDropdownIndex !== index;
    this.closeCustomMetricDropdown();
    this.isPrimaryMetricDropdownOpen = false;
    this.isSecondaryMetricDropdownOpen = false;
    if (opening) {
      this.openCustomMetricDropdownIndex = index;
    }
  }

  /** Closing without a choice counts as leaving the field, so "required" can show. */
  private closeCustomMetricDropdown(): void {
    if (this.openCustomMetricDropdownIndex === null) {
      return;
    }
    this.customMetrics?.at(this.openCustomMetricDropdownIndex)?.get('name')?.markAsTouched();
    this.openCustomMetricDropdownIndex = null;
  }

  selectCustomMetric(index: number, option: EvaluatorMetricOption): void {
    if (this.isCustomMetricOptionTaken(index, option)) {
      return;
    }
    const control = this.getCustomMetricGroup(index).get('name');
    control?.setValue(option.label);
    control?.markAsDirty();
    this.closeCustomMetricDropdown();
    this.clearStaleMetricSelections();
  }

  isCustomMetricOptionSelected(index: number, option: EvaluatorMetricOption): boolean {
    const name = this.getCustomMetricGroup(index)?.get('name')?.value;
    return resolveEvaluatorMetric(this.problemStatement, name)?.key === option.key;
  }

  /** A metric can be named once: an option another card already holds is not offered again. */
  isCustomMetricOptionTaken(index: number, option: EvaluatorMetricOption): boolean {
    return ((this.customMetrics?.value as CustomMetricFormValue[]) ?? []).some(
      (metric, i) =>
        i !== index && resolveEvaluatorMetric(this.problemStatement, metric?.name)?.key === option.key,
    );
  }

  /** Drop a primary / secondary choice whose metric was changed or removed. */
  private clearStaleMetricSelections(): void {
    const keys = new Set(this.metricDropdownOptions.map((option) => option.key));
    for (const { key } of DATASET_METRIC_SELECTION_FIELDS) {
      const control = this.metadataForm.get(key);
      if (control?.value && !keys.has(control.value)) {
        control.setValue('');
      }
    }
  }

  getCustomMetricGroup(index: number): FormGroup {
    return this.customMetrics.at(index) as FormGroup;
  }

  isCustomMetricFilled(index: number): boolean {
    const value = this.getCustomMetricGroup(index)?.get('name')?.value;
    return typeof value === 'string' && value.trim().length > 0;
  }

  isCustomMetricInvalid(index: number): boolean {
    const control = this.getCustomMetricGroup(index)?.get('name');
    return !!(control?.invalid && control?.touched);
  }

  getCustomMetricError(index: number): string {
    const control = this.getCustomMetricGroup(index)?.get('name');
    if (!control?.errors) {
      return '';
    }
    if (control.errors['required']) {
      return 'Metric name is required';
    }
    if (control.errors['maxlength']) {
      return 'Maximum 120 characters';
    }
    if (control.errors['metricNotAllowed']) {
      return this.problemStatementLabel
        ? `Invalid metric for the selected problem statement (${this.problemStatementLabel}) — choose one from the list`
        : 'Select a problem statement on the setup step to choose metrics';
    }
    return 'Invalid value';
  }

  isCustomMetricRangeInvalid(index: number): boolean {
    const group = this.getCustomMetricGroup(index);
    const minControl = group?.get('min');
    const maxControl = group?.get('max');
    const rangeTouched = !!(minControl?.touched || maxControl?.touched);
    return !!(
      rangeTouched &&
      (minControl?.invalid || maxControl?.invalid || group?.hasError('rangeOrder'))
    );
  }

  getCustomMetricRangeError(index: number): string {
    const group = this.getCustomMetricGroup(index);
    if (!group) {
      return '';
    }
    if (group.get('min')?.errors?.['pattern'] || group.get('max')?.errors?.['pattern']) {
      return 'Enter a valid number';
    }
    if (group.hasError('rangeOrder')) {
      return 'Min must be ≤ max';
    }
    return '';
  }

  /** Options for the primary/secondary metric dropdowns, derived from the custom metric names. */
  get metricDropdownOptions(): Array<{ key: string; label: string }> {
    const seen = new Set<string>();
    return ((this.customMetrics?.value as CustomMetricFormValue[]) ?? [])
      .map((metric, index) => ({ name: (metric?.name ?? '').trim(), index }))
      .filter(({ name }) => name.length > 0)
      .map(({ name, index }) => {
        let key = slugifyMetricName(name, index);
        while (seen.has(key)) {
          key = `${key}_${index + 1}`;
        }
        seen.add(key);
        return { key, label: name };
      })
      .filter(({ label }) => !!resolveEvaluatorMetric(this.problemStatement, label));
  }

  isDetailFieldFilled(key: string): boolean {
    const val = this.metadataForm.get(key)?.value;
    return typeof val === 'string' ? val.trim().length > 0 : val !== null && val !== '' && val !== undefined;
  }

  isDetailFieldInvalid(key: string): boolean {
    const control = this.metadataForm.get(key);
    return !!(control?.invalid && control?.touched);
  }

  getDetailFieldError(key: string): string {
    const control = this.metadataForm.get(key);
    if (!control?.errors) {
      return '';
    }
    if (control.errors['required']) {
      return 'This field is required';
    }
    if (control.errors['maxlength']) {
      const field = [
        ...DATASET_DETAIL_FIELDS,
        ...DATASET_METRIC_SELECTION_FIELDS,
        ...DATASET_INPUT_FIELDS,
        ...DATASET_OUTPUT_FIELDS,
      ].find((item) => item.key === key);
      return `Maximum ${field?.maxLength ?? 200} characters`;
    }
    return 'Invalid value';
  }

  isFieldRequired(field: DatasetDetailField): boolean {
    return field.required !== false;
  }

  get distributionPlots(): FormArray {
    return this.metadataForm.get('distributionPlots') as FormArray;
  }

  getPlotCategories(plotIndex: number): FormArray {
    return this.distributionPlots.at(plotIndex).get('categories') as FormArray;
  }

  buildPlotCategoryGroup(
    value?: Partial<DistributionPlotFormValue['categories'][number]>,
  ): FormGroup {
    return this.fb.group({
      name: [value?.name ?? '', Validators.maxLength(120)],
      count: [value?.count ?? '', Validators.pattern(/^\d*$/)],
      description: [value?.description ?? '', Validators.maxLength(200)],
    });
  }

  buildDistributionPlotGroup(value?: Partial<DistributionPlotFormValue>): FormGroup {
    const categories =
      value?.categories && value.categories.length > 0
        ? value.categories
        : [{ name: '', count: '', description: '' }];

    return this.fb.group({
      label: [value?.label ?? '', Validators.maxLength(120)],
      note: [value?.note ?? '', Validators.maxLength(500)],
      total: [value?.total ?? '', Validators.pattern(/^\d*$/)],
      categories: this.fb.array(categories.map((category) => this.buildPlotCategoryGroup(category))),
    });
  }

  addDistributionPlot(): void {
    this.distributionPlots.push(this.buildDistributionPlotGroup());
  }

  removeDistributionPlot(plotIndex: number): void {
    this.distributionPlots.removeAt(plotIndex);
  }

  addPlotCategory(plotIndex: number): void {
    this.getPlotCategories(plotIndex).push(this.buildPlotCategoryGroup());
  }

  removePlotCategory(plotIndex: number, categoryIndex: number): void {
    const categories = this.getPlotCategories(plotIndex);
    if (categories.length > 1) {
      categories.removeAt(categoryIndex);
    }
  }

  get hasDistributionPlots(): boolean {
    return this.distributionPlots.length > 0;
  }

  getFilledInputFieldsCount(): number {
    return this.datasetInputFields.filter((field) => this.isDetailFieldFilled(field.key)).length;
  }

  getFilledOutputFieldsCount(): number {
    return this.datasetOutputFields.filter((field) => this.isDetailFieldFilled(field.key)).length;
  }

  getFilledMetricsCount(): number {
    return this.customMetrics.controls.filter((_, index) => this.isCustomMetricFilled(index)).length;
  }

  getFilledDetailFieldsCount(): number {
    return this.datasetDetailFields.filter((field) => this.isDetailFieldFilled(field.key)).length;
  }

  getTotalRequiredFieldsCount(): number {
    const requiredDetailFields = [
      ...this.datasetDetailFields,
      ...this.datasetMetricSelectionFields,
      ...this.datasetInputFields.filter((field) => this.isFieldRequired(field)),
      ...this.datasetOutputFields.filter((field) => this.isFieldRequired(field)),
    ].length;
    return requiredDetailFields + this.customMetrics.length;
  }

  getFilledFieldsCount(): number {
    const requiredDetailFields = [
      ...this.datasetDetailFields,
      ...this.datasetMetricSelectionFields,
      ...this.datasetInputFields.filter((field) => this.isFieldRequired(field)),
      ...this.datasetOutputFields.filter((field) => this.isFieldRequired(field)),
    ];
    const filledRequiredDetails = requiredDetailFields.filter((field) =>
      this.isDetailFieldFilled(field.key),
    ).length;
    return filledRequiredDetails + this.getFilledMetricsCount();
  }

  goBack(): void {
    // Save form data before navigating back
    this.saveMetadataFormData();
    this.router.navigate([this.isBenchmarkPhase() ? '../metadata' : '../'], { relativeTo: this.route });
  }

  async proceed(): Promise<void> {
    if (this.isCreatingItem) {
      return;
    }
    if (this.isModel() ? !this.metadataForm.valid : !this.isMetadataFormValid()) {
      if (!this.isModel()) {
        this.markPhaseTouched();
      }
      return;
    }

    if (!this.isModel() && !this.isBenchmarkPhase()) {
      this.refreshCroissantIssues();
      if (this.croissantIssues.errors.length > 0) {
        this.showCroissantIssues = true;
        this.toasterService.error('The Croissant metadata is not valid. Fix the listed issues to continue.');
        this.cdr.markForCheck();
        return;
      }
    }

    this.saveMetadataFormData();

    if (this.isModel()) {
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
      return;
    }

    if (!this.isBenchmarkPhase()) {
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
      return;
    }

    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const formData = existingData ? JSON.parse(existingData) : {};
    const existingItemId = formData.itemId || formData.id || formData.item_id;

    if (existingItemId) {
      this.router.navigate(['../uploads'], { relativeTo: this.route });
      return;
    }

    this.isCreatingItem = true;
    this.cdr.markForCheck();

    try {
      const payload = await buildTanuhDatasetItemPayload(formData, this.authApiService);
      const response = await this.catalogueApiService.createAssetItem(payload);
      const itemId = extractCreatedItemId(response);

      if (!itemId) {
        throw new Error('Item ID not found in create item API response');
      }

      formData.itemId = itemId;
      formData.id = itemId;
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
      this.router.navigate(['../uploads'], { relativeTo: this.route });
    } catch (error) {
      console.error('Error creating dataset item after metadata:', error);
      this.toasterService.error('Failed to create dataset. Please try again.');
    } finally {
      this.isCreatingItem = false;
      this.cdr.markForCheck();
    }
  }

  // ---- Croissant builder --------------------------------------------------

  private restoreCroissantFormValues(saved: Record<string, unknown>): CroissantFormValues {
    const defaults = emptyCroissantFormValues();
    const text = (key: keyof CroissantFormValues, fallback: string): string =>
      typeof saved[key] === 'string' ? (saved[key] as string) : fallback;
    const list = <T>(key: keyof CroissantFormValues, fallback: T[], normalize: (raw: Partial<T>) => T): T[] => {
      const raw = saved[key];
      return Array.isArray(raw) && raw.length > 0
        ? raw.map((entry) => normalize((entry ?? {}) as Partial<T>))
        : fallback;
    };

    return {
      croissantDescription: text('croissantDescription', ''),
      croissantVersion: text('croissantVersion', defaults.croissantVersion),
      croissantLicense: text('croissantLicense', ''),
      croissantUrl: text('croissantUrl', ''),
      croissantCiteAs: text('croissantCiteAs', ''),
      croissantCreator: text('croissantCreator', ''),
      croissantDatePublished: text('croissantDatePublished', defaults.croissantDatePublished),
      croissantKeywords: text('croissantKeywords', ''),
      croissantDistributions: list<CroissantDistributionFormValue>(
        'croissantDistributions',
        defaults.croissantDistributions,
        (raw) => ({ ...emptyDistribution(), ...raw }),
      ),
      croissantRecordSets: list<CroissantRecordSetFormValue>(
        'croissantRecordSets',
        defaults.croissantRecordSets,
        (raw) => ({
          ...emptyRecordSet(),
          ...raw,
          fields:
            Array.isArray(raw.fields) && raw.fields.length > 0
              ? raw.fields.map((field) => ({ ...emptyField(), ...field }))
              : [emptyField()],
        }),
      ),
    };
  }

  private buildDistributionGroup(value: CroissantDistributionFormValue): FormGroup {
    return this.fb.group({
      id: [value.id],
      kind: [value.kind, [Validators.required]],
      name: [value.name, [Validators.required, Validators.maxLength(200)]],
      description: [value.description, [Validators.maxLength(1000)]],
      encodingFormat: [value.encodingFormat, [Validators.required, Validators.maxLength(200)]],
      contentUrl: [value.contentUrl, [Validators.maxLength(2000)]],
      includes: [value.includes, [Validators.maxLength(2000)]],
      sha256: [value.sha256, [Validators.maxLength(128)]],
    });
  }

  private buildRecordSetFieldGroup(value: CroissantFieldFormValue): FormGroup {
    return this.fb.group({
      id: [value.id],
      name: [value.name, [Validators.required, Validators.maxLength(200)]],
      dataType: [value.dataType, [Validators.required, Validators.maxLength(200)]],
      description: [value.description, [Validators.maxLength(1000)]],
      sourceDistribution: [value.sourceDistribution],
      sourceColumn: [value.sourceColumn, [Validators.maxLength(200)]],
    });
  }

  private buildRecordSetGroup(value: CroissantRecordSetFormValue): FormGroup {
    return this.fb.group({
      id: [value.id],
      name: [value.name, [Validators.required, Validators.maxLength(200)]],
      description: [value.description, [Validators.maxLength(1000)]],
      fields: this.fb.array(value.fields.map((field) => this.buildRecordSetFieldGroup(field))),
    });
  }

  private buildCurrentCroissantDocument(): JsonRecord {
    return buildCroissantDocument(
      this.datasetData?.title ?? '',
      this.metadataForm.getRawValue() as Partial<CroissantFormValues>,
      this.croissantBase,
    );
  }

  private refreshCroissantIssues(): void {
    if (this.isModel() || !this.metadataForm) {
      return;
    }
    this.croissantIssues = validateCroissant(this.buildCurrentCroissantDocument());
    this.cdr.markForCheck();
  }

  get croissantDistributions(): FormArray {
    return this.metadataForm.get('croissantDistributions') as FormArray;
  }

  get croissantRecordSets(): FormArray {
    return this.metadataForm.get('croissantRecordSets') as FormArray;
  }

  getRecordSetFields(recordSetIndex: number): FormArray {
    return this.croissantRecordSets.at(recordSetIndex).get('fields') as FormArray;
  }

  /** Distribution entries a record-set field can read its values from. */
  get croissantDistributionNames(): string[] {
    return (this.croissantDistributions?.controls ?? [])
      .map((group) => String(group.get('name')?.value ?? '').trim())
      .filter((name) => name.length > 0);
  }

  isDistributionFileSet(index: number): boolean {
    return this.croissantDistributions.at(index).get('kind')?.value === 'FileSet';
  }

  isPathInvalid(...path: Array<string | number>): boolean {
    const control = this.metadataForm.get(path);
    return !!(control?.invalid && control?.touched);
  }

  addDistribution(): void {
    this.croissantDistributions.push(this.buildDistributionGroup(emptyDistribution()));
    this.refreshCroissantIssues();
  }

  removeDistribution(index: number): void {
    if (this.croissantDistributions.length > 1) {
      this.croissantDistributions.removeAt(index);
      this.refreshCroissantIssues();
    }
  }

  addRecordSet(): void {
    this.croissantRecordSets.push(this.buildRecordSetGroup(emptyRecordSet()));
    this.refreshCroissantIssues();
  }

  removeRecordSet(index: number): void {
    if (this.croissantRecordSets.length > 1) {
      this.croissantRecordSets.removeAt(index);
      this.refreshCroissantIssues();
    }
  }

  addRecordSetField(recordSetIndex: number): void {
    this.getRecordSetFields(recordSetIndex).push(this.buildRecordSetFieldGroup(emptyField()));
    this.refreshCroissantIssues();
  }

  removeRecordSetField(recordSetIndex: number, fieldIndex: number): void {
    const fields = this.getRecordSetFields(recordSetIndex);
    if (fields.length > 1) {
      fields.removeAt(fieldIndex);
      this.refreshCroissantIssues();
    }
  }

  /**
   * Save metadata form data to localStorage
   */
  private saveMetadataFormData(): void {
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const existingFormData = existingData ? JSON.parse(existingData) : {};
    
    let metadataValues: Record<string, unknown> = { ...this.metadataForm.value };
    if (!this.isModel()) {
      metadataValues['datasetMetrics'] = buildTanuhDatasetMetrics(metadataValues);
      metadataValues['croissant'] = this.buildCurrentCroissantDocument();
    }
    
    const formData = {
      ...existingFormData,
      ...this.datasetData,
      ...metadataValues
    };
    
    localStorage.setItem(localStorageKey, JSON.stringify(formData));
  }

  /** UI only: show "Public" for "Open", API still uses OPEN */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    return !permission ? '' : permission === 'Open' ? 'Public' : permission;
  }

  private hasCreatedItemId(): boolean {
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    if (!existingData) {
      return false;
    }
    try {
      const formData = JSON.parse(existingData) as Record<string, unknown>;
      return Boolean(formData['itemId'] || formData['id'] || formData['item_id']);
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScroll, true);
    cancelAnimationFrame(this.scrollFrame);
    if (this.scrollLockTimer) {
      clearTimeout(this.scrollLockTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}

